import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Aperture, LayoutDashboard, Users, CalendarDays, Calendar, LogOut, Search, Bell, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const nav = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/calendar", label: "Work Calendar", icon: Calendar, testId: "nav-calendar" },
    { to: "/staff", label: "Staff", icon: Users, testId: "nav-staff" },
    { to: "/availability", label: "Availability", icon: CalendarDays, testId: "nav-availability" },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleLogout = async () => {
        setMobileOpen(false);
        await logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen flex bg-gray-50">
            {/* Mobile Backdrop */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
                    onClick={() => setMobileOpen(false)}
                />
            )}

            {/* Sidebar (Desktop + Mobile Sliding Drawer) */}
            <aside
                className={`w-64 border-r border-gray-200 bg-white flex flex-col fixed left-0 top-0 h-screen z-50 transition-transform duration-300 ease-in-out lg:translate-x-0 ${
                    mobileOpen ? "translate-x-0" : "-translate-x-full"
                }`}
                data-testid="app-sidebar"
            >
                <div className="px-6 py-6 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-md bg-blue-500 flex items-center justify-center">
                            <Aperture className="w-5 h-5 text-white" strokeWidth={2.2} />
                        </div>
                        <div>
                            <div className="font-serif text-lg leading-none text-gray-900">Groupfie</div>
                            <div className="text-[10px] uppercase tracking-widest text-gray-400 mt-1">Studio CRM</div>
                        </div>
                    </div>

                    {/* Mobile Close Button */}
                    <button
                        onClick={() => setMobileOpen(false)}
                        className="lg:hidden p-1.5 text-gray-400 hover:text-gray-700 rounded-md"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <nav className="flex-1 px-3 py-4 space-y-0.5">
                    {nav.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            data-testid={item.testId}
                            onClick={() => setMobileOpen(false)}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                    isActive
                                        ? "bg-blue-50 text-blue-600"
                                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                                }`
                            }
                        >
                            <item.icon className="w-4 h-4" strokeWidth={1.8} />
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="border-t border-gray-100 px-4 py-4">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-sm font-semibold">
                            {user?.brandName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "G"}
                        </div>
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{user?.brandName || "Studio Admin"}</div>
                            <div className="text-xs text-gray-500 truncate">{user?.email}</div>
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start text-gray-600 hover:text-gray-900"
                        onClick={handleLogout}
                        data-testid="logout-button"
                    >
                        <LogOut className="w-4 h-4 mr-2" /> Sign out
                    </Button>
                </div>
            </aside>

            {/* Main Content Area */}
            <div className="ml-0 lg:ml-64 flex-1 min-h-screen flex flex-col min-w-0">
                <header className="h-16 border-b border-gray-200 bg-white/80 backdrop-blur-md sticky top-0 z-20">
                    <div className="h-full px-4 sm:px-8 flex items-center justify-between gap-3">
                        
                        {/* Hamburger Button (Mobile Only) */}
                        <button
                            onClick={() => setMobileOpen(true)}
                            className="p-2 text-gray-600 hover:text-gray-900 lg:hidden rounded-md hover:bg-gray-100"
                            title="Open Menu"
                        >
                            <Menu className="w-5 h-5" />
                        </button>

                        {/* Search Bar */}
                        <div className="relative w-full max-w-xs sm:max-w-sm">
                            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                placeholder="Search clients, events, staff…"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        window.dispatchEvent(new CustomEvent("global-search", { detail: q }));
                                    }
                                }}
                                data-testid="topbar-search"
                                className="w-full h-9 pl-9 pr-3 text-xs sm:text-sm rounded-md border border-gray-200 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none placeholder-gray-400"
                            />
                        </div>

                        {/* Topbar Actions */}
                        <div className="flex items-center gap-2">
                            <button
                                className="w-9 h-9 rounded-md hover:bg-gray-100 flex items-center justify-center text-gray-500 relative"
                                data-testid="topbar-notifications"
                                title="Notifications"
                            >
                                <Bell className="w-4 h-4" />
                                <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                            </button>
                        </div>
                    </div>
                </header>

                <main className="flex-1 min-w-0">{children}</main>
            </div>
        </div>
    );
}